import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { AreaSequenceItemStatus } from '../../interfaces/AreaSequenceItemStatus.enum';
import { requiredStringSchema, tzHeadersSchema } from '../schemas';
import { sequenceItemSharedSchema } from '../../interfaces/ISequenceItem';

// ============================================================
// Helper schemas for coercion (to match Joi behavior)
// ============================================================

const coercedNumberSchema = z.preprocess((val) => {
	if (typeof val === 'number') return val;
	if (typeof val === 'string') {
		const parsed = Number(val);
		if (!Number.isNaN(parsed)) return parsed;
	}
	return val;
}, z.number());

const optionalNonEmptyStringSchema = z.string().min(1).optional();

// ============================================================
// Reusable Param Schemas
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
});

const idParamsSchema = projectParamsSchema.extend({
	id: requiredStringSchema.describe('Resource identifier'),
});

const sequenceIdParamsSchema = projectParamsSchema.extend({
	sequenceId: requiredStringSchema.describe('Sequence ID'),
});

const areaIdParamsSchema = projectParamsSchema.extend({
	areaId: requiredStringSchema.describe('Area ID'),
});

const sequenceTypeParamsSchema = projectParamsSchema.extend({
	sequenceType: optionalNonEmptyStringSchema.describe('Sequence type'),
});

// ============================================================
// Query Schemas
// ============================================================

const getAreaSequenceItemBySequenceIdQuerySchema = z.object({
	sequenceId: optionalNonEmptyStringSchema.describe('Sequence ID filter'),
});

const getAreaSequenceCountQuerySchema = z.object({});

const getAreaSequenceCountBySequenceItemIdQuerySchema = z.object({
	sequenceItemId: requiredStringSchema.describe('Sequence item ID'),
});

const getAreaSequenceCountByFloorIdQuerySchema = z.object({
	floorId: requiredStringSchema.describe('Floor ID'),
});

const getAreaSequenceItemBySequenceItemIdQuerySchema = z.object({
	sequenceItemId: optionalNonEmptyStringSchema.describe('Sequence item ID'),
});

const getAreaSequenceItemByFloorIdsQuerySchema = z.object({
	floorIds: requiredStringSchema.describe('Comma-separated floor IDs'),
	sequenceId: requiredStringSchema.describe('Sequence ID'),
});

const getAreaSequenceItemByGroupIdQuerySchema = z.object({
	activityGroupId: requiredStringSchema.describe('Activity group ID'),
});

const getAreaSequenceItemByUpdateTimeQuerySchema = z.object({
	timestampFrom: coercedNumberSchema.describe('Start timestamp (Unix milliseconds)'),
	timestampTo: coercedNumberSchema.describe('End timestamp (Unix milliseconds)'),
});

// ============================================================
// Body Schemas for Mutations
// ============================================================

/**
 * The body carried an entire client-supplied `mergedAreaSequenceItem` whose nested
 * `projectId` the handler asserted membership on — validating the caller's own
 * CLAIM rather than the stored row, and invisible to `collectRequestProjectIds`,
 * which reads only top-level body keys. The service always reloaded the row from
 * the DB anyway and consumed only `_id` and `projectId` from the payload. The id
 * is now the only thing sent; the project comes from the path. Omitting the field
 * from the schema is what prevents it being sent, rather than merely ignored.
 */
const putDoneBodySchema = z.object({
	areaSequenceItemId: requiredStringSchema.describe('Area sequence item ID'),
	isDone: z.boolean().describe('Whether the item is done'),
});

const putChangeIsReadyForReviewStatusBodySchema = z.object({
	isReadyForReview: z.boolean().describe('Ready for review status'),
});

const putStatusUpdateBodySchema = z.object({
	status: z.nativeEnum(AreaSequenceItemStatus).describe('New status'),
});

const putBulkStatusUpdateBodySchema = z.object({
	status: z.nativeEnum(AreaSequenceItemStatus).describe('New status'),
	ids: z.array(z.string()).describe('Array of sequence item IDs'),
});

const putBulkLinkSequenceItemToMilestoneBodySchema = z.object({
	milestoneId: z.string().nullable().optional().describe('Milestone ID (null to unlink, omit to keep unchanged)'),
	sequenceItemIds: z.array(z.string()).describe('Sequence item IDs'),
});

const putBulkSequenceItemsBodySchema = z.object({
	orderIndex: z.number().optional().describe('Order index'),
	sequenceName: z.string().optional().describe('Sequence name'),
	sequenceItems: z.array(z.object({}).passthrough()).nullable().optional().describe('Sequence items to update'),
});

const putBulkSequenceItemsQuerySchema = z.object({
	sequenceId: requiredStringSchema.describe('Sequence ID'),
});

/**
 * Stays PASSTHROUGH: legacy clients send fields nobody has enumerated. `projectId`
 * is dropped in the handler and again at the `$set` — silently, so an old payload
 * keeps working minus the field. Here it is top-level, so the guard DOES see it
 * (divergence, not escalation), but the URL must still be the only project.
 */
const putSequenceItemByIdBodySchema = z.object({}).passthrough();

/**
 * `sequenceItem` stays PASSTHROUGH deliberately: legacy clients send fields this
 * repository has never enumerated, and declaring the shape here would 400 them.
 * The permissiveness is load-bearing.
 *
 * The one dangerous key, `projectId`, is dropped in the handler and again where
 * the `$set` is built — silently, so an old payload keeps working minus the
 * field. `.omit({ projectId: true })` would NOT do it: on a loose/passthrough
 * object an omitted key simply survives as an unrecognized one. Nested here it is
 * also invisible to `collectRequestProjectIds`, which reads top-level body keys.
 */
const putSequenceItemAreasByIdBodySchema = z.object({
	sequenceItem: z.object({}).passthrough().optional().describe('Sequence item data'),
	hasPartialAreas: z.boolean().describe('Whether areas are partial'),
	areaIds: z.array(z.string()).min(1).optional().describe('Area IDs'),
});

/**
 * `projectId` is omitted from the nested sequence item: it is stamped from the
 * path on insert. Declaring it here made it a nested scope claim the central
 * guard cannot see, gated only by a handler-level assert.
 */
const postSequenceItemBodySchema = z.object({
	sequenceItem: sequenceItemSharedSchema.omit({ projectId: true }).describe('Sequence item data'),
	areaIds: z.array(z.string()).min(1).optional().describe('Area IDs to link'),
});

const postAreaSequenceItemsForAreaBodySchema = z.object({
	area: z.object({}).passthrough().describe('Area configuration'),
});

const deleteAreaSequenceItemsQuerySchema = z.object({
	areaIds: requiredStringSchema.describe('Comma-separated area IDs'),
});

// ============================================================
// Contract Definition
// Route order matters! Specific routes must come before parameterized routes.
// ============================================================

/**
 * `{ root: true }` so the project can precede the collection segment.
 */
export const sequenceItemContract = defineContract(
	'sequenceItems',
	{
		// GET routes
		getSequenceByType: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/sequenceTypes/:sequenceType',
			params: sequenceTypeParamsSchema,
			response: z.any(),
			operationId: 'getSequencesByType',
			tags: ['Sequence Items'],
			summary: 'Get sequences by type',
			description: 'Retrieves sequences filtered by optional type for a specific project',
		}),
		getSequenceItemsBySequenceId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/sequenceId/:sequenceId',
			params: sequenceIdParamsSchema,
			response: z.any(),
			operationId: 'getSequenceItemsBySequenceId',
			tags: ['Sequence Items'],
			summary: 'Get sequence items by sequence ID',
			description: 'Retrieves all sequence items for a specific sequence',
		}),
		getSequenceItemsByProject: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/byProject',
			params: projectParamsSchema,
			response: z.any(),
			operationId: 'getSequenceItemsPerProject',
			tags: ['Sequence Items'],
			summary: 'Get sequence items per project',
			description: 'Retrieves all sequence items for a specific project',
		}),
		getAllProjectSequenceItems: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/allProjectSequenceItems',
			params: projectParamsSchema,
			response: z.any(),
			operationId: 'getAllProjectSequenceItems',
			tags: ['Sequence Items'],
			summary: 'Get all project sequence items',
			description: 'Retrieves all sequence items for a specific project',
		}),
		getAreaSequenceItemsBySequenceId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem',
			params: projectParamsSchema,
			query: getAreaSequenceItemBySequenceIdQuerySchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemsBySequenceId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence items by sequence ID',
			description: 'Retrieves area sequence items filtered by optional sequence ID',
		}),
		getAreaSequenceItemsCount: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItemsCount',
			params: projectParamsSchema,
			query: getAreaSequenceCountQuerySchema,
			response: z.object({ count: z.number() }),
			operationId: 'getAreaSequenceCount',
			tags: ['Sequence Items'],
			summary: 'Get area sequence count',
			description: 'Retrieves the count of area sequences, optionally filtered by project',
		}),
		getAreaSequenceCountBySequenceItemId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/count/bySequenceItemId',
			params: projectParamsSchema,
			query: getAreaSequenceCountBySequenceItemIdQuerySchema,
			response: z.object({ count: z.number() }),
			operationId: 'getAreaSequenceCountBySequenceItemId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence count by sequence item ID',
			description: 'Retrieves the count of area sequences for a specific sequence item',
		}),
		getAreaSequenceCountPerSequenceItems: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/count/perSequenceItem',
			params: projectParamsSchema,
			response: z.any(),
			operationId: 'getAreaSequenceCountPerSequenceItems',
			tags: ['Sequence Items'],
			summary: 'Get area sequence count per sequence items',
			description: 'Retrieves the count of area sequences grouped by sequence items',
		}),
		getAreaSequenceCountByFloorId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/count/byFloorId',
			params: projectParamsSchema,
			query: getAreaSequenceCountByFloorIdQuerySchema,
			response: z.object({ count: z.number() }),
			operationId: 'getAreaSequenceCountByFloorId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence count by floor ID',
			description: 'Retrieves the count of area sequences for a specific floor',
		}),
		getAreaSequenceItemBySequenceItemId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/bySequenceItem',
			params: projectParamsSchema,
			query: getAreaSequenceItemBySequenceItemIdQuerySchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemsBySequenceItemId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence items by sequence item ID',
			description: 'Retrieves area sequence items filtered by optional sequence item ID',
		}),
		getAreaSequenceItemByFloorIds: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/byFloorId',
			params: projectParamsSchema,
			query: getAreaSequenceItemByFloorIdsQuerySchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemsByFloorIds',
			tags: ['Sequence Items'],
			summary: 'Get area sequence items by floor IDs',
			description: 'Retrieves area sequence items for multiple floors and a specific sequence',
		}),
		getAreaSequenceItemByGroupId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/byActivityGroupId',
			params: projectParamsSchema,
			query: getAreaSequenceItemByGroupIdQuerySchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemsByGroupId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence items by group ID',
			description: 'Retrieves area sequence items for a specific activity group',
		}),
		getAreaSequenceItemByUpdateTime: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/byStatusUpdateTime',
			params: projectParamsSchema,
			query: getAreaSequenceItemByUpdateTimeQuerySchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemsByUpdateTime',
			tags: ['Sequence Items'],
			summary: 'Get area sequence items by update time',
			description: 'Retrieves area sequence items updated within a specific time range',
		}),
		// IMPORTANT: /areaSequenceItem/byAreaId/:areaId must be before /areaSequenceItem/:id
		getAreaSequenceItemsByAreaId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/byAreaId/:areaId',
			params: areaIdParamsSchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemsByAreaId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence items by area ID',
			description: 'Retrieves all area sequence items for a specific area',
		}),
		getAreaSequenceItemByAreaSequenceId: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/:id',
			params: idParamsSchema,
			response: z.any(),
			operationId: 'getAreaSequenceItemByAreaSequenceId',
			tags: ['Sequence Items'],
			summary: 'Get area sequence item by area sequence ID',
			description: 'Retrieves a specific area sequence item by its ID',
		}),
		getSequenceItemById: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItems/:id',
			params: idParamsSchema,
			response: z.any(),
			operationId: 'getSequenceItemById',
			tags: ['Sequence Items'],
			summary: 'Get sequence item by ID',
			description: 'Retrieves a specific sequence item by its ID',
		}),

		// PUT routes
		bulkLinkSequenceItemToMilestone: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/bulkLinkSequenceItemToMilestone',
			params: projectParamsSchema,
			body: putBulkLinkSequenceItemToMilestoneBodySchema,
			response: z.string(),
			operationId: 'bulkLinkSequenceItemsToMilestone',
			tags: ['Sequence Items'],
			summary: 'Bulk link sequence items to milestone',
			description: 'Links or unlinks multiple sequence items to/from a milestone',
		}),
		// IMPORTANT: /areaSequenceItem/done must be before /areaSequenceItem/:id
		markAreaSequenceItemDone: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/done',
			params: projectParamsSchema,
			body: putDoneBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'markSequenceItemDone',
			tags: ['Sequence Items'],
			summary: 'Mark sequence item as done',
			description: 'Updates the done status of a merged area sequence item',
		}),
		updateReadyForReviewStatus: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/:id/readyForReview',
			params: idParamsSchema,
			body: putChangeIsReadyForReviewStatusBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'updateAreaSequenceItemReadyForReviewStatus',
			tags: ['Sequence Items'],
			summary: 'Change ready for review status',
			description: 'Updates the ready for review status of an area sequence item',
		}),
		// IMPORTANT: /areaSequenceItem/status/bulk must be before /areaSequenceItem/:id/status
		bulkUpdateAreaSequenceItemStatuses: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/status/bulk',
			params: projectParamsSchema,
			body: putBulkStatusUpdateBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'bulkUpdateAreaSequenceItemStatuses',
			tags: ['Sequence Items'],
			summary: 'Bulk update sequence item statuses',
			description: 'Updates the status of multiple area sequence items at once',
		}),
		updateAreaSequenceItemStatus: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem/:id/status',
			params: idParamsSchema,
			body: putStatusUpdateBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'updateAreaSequenceItemStatus',
			tags: ['Sequence Items'],
			summary: 'Update sequence item status',
			description: 'Updates the status of a single area sequence item',
		}),
		bulkUpdateSequenceItems: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/bulk',
			params: projectParamsSchema,
			query: putBulkSequenceItemsQuerySchema,
			body: putBulkSequenceItemsBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'bulkUpdateSequenceItems',
			tags: ['Sequence Items'],
			summary: 'Bulk update sequence items',
			description: 'Updates multiple sequence items with optional order index and name changes',
		}),
		// IMPORTANT: /:id/areas must be before /:id
		updateSequenceItemAreas: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/:id/areas',
			params: idParamsSchema,
			body: putSequenceItemAreasByIdBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'updateSequenceItemAreasById',
			tags: ['Sequence Items'],
			summary: 'Update sequence item areas',
			description: 'Updates the areas associated with a sequence item',
		}),
		updateSequenceItemById: route({
			method: 'PUT',
			path: '/projects/:projectId/sequenceItems/:id',
			params: idParamsSchema,
			body: putSequenceItemByIdBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'updateSequenceItemById',
			tags: ['Sequence Items'],
			summary: 'Update sequence item by ID',
			description: 'Updates a specific sequence item',
		}),

		// POST routes
		createSequenceItem: route({
			method: 'POST',
			path: '/projects/:projectId/sequenceItems',
			params: projectParamsSchema,
			body: postSequenceItemBodySchema,
			response: z.any(),
			operationId: 'createSequenceItem',
			tags: ['Sequence Items'],
			summary: 'Create sequence item',
			description: 'Creates a new sequence item with optional area links',
		}),
		createAreaSequenceItemsForArea: route({
			method: 'POST',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem',
			params: projectParamsSchema,
			body: postAreaSequenceItemsForAreaBodySchema,
			response: z.string(),
			operationId: 'createAreaSequenceItemsForArea',
			tags: ['Sequence Items'],
			summary: 'Create area sequence items for area',
			description: 'Creates area sequence items for a specific area configuration',
		}),

		// DELETE routes
		deleteAreaSequenceItems: route({
			method: 'DELETE',
			path: '/projects/:projectId/sequenceItems/areaSequenceItem',
			params: projectParamsSchema,
			query: deleteAreaSequenceItemsQuerySchema,
			response: z.string(),
			operationId: 'deleteAreaSequenceItems',
			tags: ['Sequence Items'],
			summary: 'Delete area sequence items',
			description: 'Deletes area sequence items for specified areas',
		}),
		deleteSequenceItemById: route({
			method: 'DELETE',
			path: '/projects/:projectId/sequenceItems/:id',
			params: idParamsSchema,
			response: z.string(),
			operationId: 'deleteSequenceItemById',
			tags: ['Sequence Items'],
			summary: 'Delete sequence item by ID',
			description: 'Deletes a specific sequence item and its associated area sequence items',
		}),
	},
	{ root: true }
);
